import { useState } from "react";
import { useAuth } from "../hooks/useAuth";

// ── Tool Icon ─────────────────────────────────────────────────────────────────
function ToolIcon({ bg, children, size = 58 }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        background: bg,
        borderRadius: 14,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: size * 0.43,
        color: "#fff",
        fontWeight: 900,
        boxShadow: "0 4px 16px rgba(0,0,0,0.14)",
        flexShrink: 0,
        userSelect: "none",
        letterSpacing: "-1px",
      }}
    >
      {children}
    </div>
  );
}

// ── Tools catalogue ───────────────────────────────────────────────────────────
const TOOLS = [
  {
    id: "edit",
    bg: "linear-gradient(135deg,#f97316,#dc2626)",
    icon: "✏️",
    name: "Edit PDF",
    desc: "Add text, images, shapes and freehand annotations directly to any PDF page.",
    cats: ["all", "edit"],
  },
  {
    id: "compress",
    bg: "linear-gradient(135deg,#22c55e,#16a34a)",
    icon: "🗜️",
    name: "Compress PDF",
    desc: "Reduce file size while preserving quality. Set an exact target size like 100 KB.",
    cats: ["all", "optimize"],
  },
  {
    id: "word",
    bg: "linear-gradient(135deg,#2563eb,#1e40af)",
    icon: "W",
    name: "PDF to Word",
    desc: "Convert your PDF into an editable DOCX document with preserved text layout.",
    cats: ["all", "convert"],
  },
  {
    id: "excel",
    bg: "linear-gradient(135deg,#16a34a,#15803d)",
    icon: "X",
    name: "PDF to Excel",
    desc: "Extract tables and data from PDFs into a structured Excel spreadsheet.",
    cats: ["all", "convert"],
  },
  {
    id: "merge",
    bg: "linear-gradient(135deg,#8b5cf6,#7c3aed)",
    icon: "🔗",
    name: "Merge PDFs",
    desc: "Combine multiple PDF documents into a single file in any order you choose.",
    cats: ["all", "organize"],
  },
  {
    id: "image",
    bg: "linear-gradient(135deg,#0ea5e9,#0284c7)",
    icon: "🖼️",
    name: "PDF to Image",
    desc: "Convert each page to a high-quality PNG image or download all as a ZIP archive.",
    cats: ["all", "convert"],
  },
  {
    id: "lock",
    bg: "linear-gradient(135deg,#f59e0b,#d97706)",
    icon: "🔒",
    name: "Protect PDF",
    desc: "Password-protect your PDF to prevent unauthorised access or editing.",
    cats: ["all", "security"],
  },
  {
    id: "redact",
    bg: "linear-gradient(135deg,#64748b,#334155)",
    icon: "⬛",
    name: "Redact PDF",
    desc: "Permanently black out sensitive text and images from your PDF document.",
    cats: ["all", "security", "edit"],
  },
  {
    id: "delete",
    bg: "linear-gradient(135deg,#ef4444,#b91c1c)",
    icon: "✂️",
    name: "Delete Pages",
    desc: "Remove unwanted pages from your PDF document quickly and cleanly.",
    cats: ["all", "organize"],
  },
];

const CATEGORIES = [
  { id: "all", label: "All Tools" },
  { id: "organize", label: "Organize PDF" },
  { id: "optimize", label: "Optimize PDF" },
  { id: "convert", label: "Convert PDF" },
  { id: "edit", label: "Edit PDF" },
  { id: "security", label: "PDF Security" },
];

// ── Feature stripe ─────────────────────────────────────────────────────────────
const FEATURES = [
  { icon: "⚡", title: "Lightning Fast", desc: "All processing happens in your browser — no upload delays." },
  { icon: "🔐", title: "100% Private", desc: "Your files never leave your device unless you choose to upload." },
  { icon: "🆓", title: "Free Forever", desc: "Core tools are free. Upgrade for unlimited exports and no ads." },
  { icon: "🌐", title: "Works Everywhere", desc: "Any browser, any device — no installation required." },
];

// ── Main Component ─────────────────────────────────────────────────────────────
export default function HomePage({ onSelectTool }) {
  const { user, logout } = useAuth();
  const [cat, setCat] = useState("all");
  const [hovered, setHovered] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const filtered = TOOLS.filter((t) => t.cats.includes(cat));

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f8fafc",
        fontFamily: "'Sora','Inter',sans-serif",
        color: "#1e293b",
      }}
    >
      {/* ── GOOGLE FONTS ───────────────────────────────────────── */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
        .tool-card:hover { transform: translateY(-3px); }
        .nav-link:hover { color: #1e293b !important; }
        .cat-btn:hover { border-color: #94a3b8 !important; color: #334155 !important; }
        .feature-card:hover { box-shadow: 0 8px 32px rgba(59,130,246,0.1); border-color: #bfdbfe; }
      `}</style>

      {/* ── NAVBAR ─────────────────────────────────────────────── */}
      <nav
        style={{
          background: "#fff",
          borderBottom: "1px solid #e2e8f0",
          height: 66,
          display: "flex",
          alignItems: "center",
          padding: "0 28px",
          gap: 24,
          position: "sticky",
          top: 0,
          zIndex: 200,
          boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        }}
      >
        {/* Logo */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
            cursor: "pointer",
          }}
        >
          <div
            style={{
              width: 38,
              height: 38,
              background: "linear-gradient(135deg,#3b82f6,#6366f1)",
              borderRadius: 10,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
            }}
          >
            📄
          </div>
          <div>
            <div
              style={{
                fontSize: 17,
                fontWeight: 800,
                letterSpacing: "-0.5px",
                lineHeight: 1,
              }}
            >
              <span
                style={{
                  background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                FreePDF
              </span>
              <span style={{ color: "#1e293b" }}>Master.in</span>
            </div>
            <div style={{ fontSize: 10, color: "#94a3b8", fontWeight: 500 }}>
              Professional · Free · Online
            </div>
          </div>
        </div>

        {/* Nav links — hidden on mobile */}
        <div
          style={{
            display: "flex",
            gap: 6,
            flex: 1,
            justifyContent: "center",
          }}
        >
          {[
            { label: "Merge PDF", cat: "organize" },
            { label: "Compress PDF", cat: "optimize" },
            { label: "Convert PDF", cat: "convert" },
            { label: "Edit PDF", cat: "edit" },
          ].map(({ label, cat: c }) => (
            <button
              key={label}
              className="nav-link"
              onClick={() => {
                setCat(c);
                document
                  .getElementById("tools-grid")
                  ?.scrollIntoView({ behavior: "smooth" });
              }}
              style={{
                background: "none",
                border: "none",
                color: "#64748b",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'Sora',sans-serif",
                padding: "8px 12px",
                borderRadius: 8,
                whiteSpace: "nowrap",
                transition: "color 0.15s",
              }}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Right: user info + badge + sign out */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            flexShrink: 0,
          }}
        >
          {user?.plan === "free" ? (
            <span
              style={{
                fontSize: 11,
                background: "rgba(99,102,241,0.08)",
                color: "#6366f1",
                padding: "3px 10px",
                borderRadius: 20,
                fontWeight: 700,
                border: "1px solid rgba(99,102,241,0.2)",
              }}
            >
              FREE PLAN
            </span>
          ) : (
            <span
              style={{
                fontSize: 11,
                background: "linear-gradient(135deg,#f59e0b,#d97706)",
                color: "#fff",
                padding: "3px 10px",
                borderRadius: 20,
                fontWeight: 700,
              }}
            >
              ⭐ PRO
            </span>
          )}
          <span
            style={{
              fontSize: 13,
              color: "#475569",
              fontWeight: 500,
              maxWidth: 120,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {user?.name}
          </span>
          <button
            onClick={logout}
            style={{
              padding: "8px 18px",
              background: "linear-gradient(135deg,#3b82f6,#6366f1)",
              color: "#fff",
              border: "none",
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'Sora',sans-serif",
              whiteSpace: "nowrap",
              transition: "opacity 0.15s",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
          >
            Sign Out
          </button>
        </div>
      </nav>

      {/* ── HERO ───────────────────────────────────────────────── */}
      <section
        style={{
          background:
            "linear-gradient(160deg,#ffffff 0%,#eff6ff 45%,#faf5ff 100%)",
          padding: "72px 28px 56px",
          textAlign: "center",
          borderBottom: "1px solid #e2e8f0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative blobs */}
        <div
          style={{
            position: "absolute",
            top: -120,
            left: -120,
            width: 440,
            height: 440,
            background:
              "radial-gradient(circle,rgba(59,130,246,0.08) 0%,transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -80,
            right: -80,
            width: 360,
            height: 360,
            background:
              "radial-gradient(circle,rgba(139,92,246,0.07) 0%,transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <div style={{ position: "relative", zIndex: 1 }}>
          {/* Badge */}
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "rgba(59,130,246,0.08)",
              border: "1px solid rgba(59,130,246,0.2)",
              borderRadius: 20,
              padding: "5px 14px",
              fontSize: 12,
              fontWeight: 700,
              color: "#3b82f6",
              marginBottom: 22,
              letterSpacing: 0.3,
            }}
          >
            🚀 Free · No sign-up required for basic tools
          </div>

          <h1
            style={{
              fontSize: "clamp(30px,4.5vw,54px)",
              fontWeight: 800,
              color: "#0f172a",
              margin: "0 auto 20px",
              maxWidth: 760,
              lineHeight: 1.12,
              letterSpacing: "-1.5px",
            }}
          >
            Every tool you need to work with PDFs in one place
          </h1>

          <p
            style={{
              fontSize: 17,
              color: "#64748b",
              maxWidth: 540,
              margin: "0 auto 36px",
              lineHeight: 1.75,
              fontWeight: 400,
            }}
          >
            Merge, split, compress, convert, edit and secure your PDFs — all
            100% free and easy to use.
          </p>

          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <button
              onClick={() => onSelectTool("edit")}
              style={{
                padding: "15px 36px",
                background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                color: "#fff",
                border: "none",
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'Sora',sans-serif",
                letterSpacing: 0.3,
                boxShadow: "0 6px 24px rgba(59,130,246,0.35)",
                transition: "transform 0.15s,box-shadow 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow =
                  "0 10px 32px rgba(59,130,246,0.4)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "none";
                e.currentTarget.style.boxShadow =
                  "0 6px 24px rgba(59,130,246,0.35)";
              }}
            >
              🚀 Start Editing — Free
            </button>
            <button
              onClick={() => onSelectTool("compress")}
              style={{
                padding: "15px 28px",
                background: "#fff",
                color: "#374151",
                border: "2px solid #e2e8f0",
                borderRadius: 12,
                fontSize: 15,
                fontWeight: 600,
                cursor: "pointer",
                fontFamily: "'Sora',sans-serif",
                transition: "border-color 0.15s",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.borderColor = "#94a3b8")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.borderColor = "#e2e8f0")
              }
            >
              🗜️ Compress PDF
            </button>
          </div>

          {/* Stats row */}
          <div
            style={{
              display: "flex",
              gap: 40,
              justifyContent: "center",
              marginTop: 44,
              flexWrap: "wrap",
            }}
          >
            {[
              ["9+", "PDF Tools"],
              ["100%", "Free"],
              ["0", "Registration"],
              ["∞", "File Edits"],
            ].map(([num, label]) => (
              <div key={label} style={{ textAlign: "center" }}>
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: 800,
                    background: "linear-gradient(135deg,#3b82f6,#6366f1)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    lineHeight: 1,
                  }}
                >
                  {num}
                </div>
                <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4, fontWeight: 500 }}>
                  {label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── TOOL SECTION ───────────────────────────────────────── */}
      <section
        id="tools-grid"
        style={{ maxWidth: 1280, margin: "0 auto", padding: "44px 28px 60px" }}
      >
        {/* Category filter pills */}
        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            marginBottom: 32,
          }}
        >
          {CATEGORIES.map((c) => (
            <button
              key={c.id}
              className="cat-btn"
              onClick={() => setCat(c.id)}
              style={{
                padding: "9px 22px",
                border: "2px solid",
                borderColor: cat === c.id ? "#3b82f6" : "#e2e8f0",
                borderRadius: 28,
                fontSize: 13,
                fontWeight: 700,
                cursor: "pointer",
                fontFamily: "'Sora',sans-serif",
                background: cat === c.id ? "#3b82f6" : "#fff",
                color: cat === c.id ? "#fff" : "#64748b",
                transition: "all 0.15s",
                boxShadow:
                  cat === c.id ? "0 4px 12px rgba(59,130,246,0.25)" : "none",
              }}
            >
              {c.label}
            </button>
          ))}
        </div>

        {/* Tool cards grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))",
            gap: 20,
          }}
        >
          {filtered.map((tool) => (
            <div
              key={tool.id}
              className="tool-card"
              onClick={() => onSelectTool(tool.id)}
              onMouseEnter={() => setHovered(tool.id)}
              onMouseLeave={() => setHovered(null)}
              style={{
                background: "#fff",
                border: `2px solid ${hovered === tool.id ? "#bfdbfe" : "#f1f5f9"}`,
                borderRadius: 18,
                padding: "26px 24px",
                cursor: "pointer",
                transition: "all 0.2s",
                boxShadow:
                  hovered === tool.id
                    ? "0 12px 40px rgba(59,130,246,0.12)"
                    : "0 2px 8px rgba(0,0,0,0.04)",
              }}
            >
              <ToolIcon bg={tool.bg} size={58}>
                {typeof tool.icon === "string" && tool.icon.length <= 2 ? (
                  <span
                    style={{
                      fontWeight: 900,
                      fontSize: 26,
                      fontFamily: "Arial,sans-serif",
                    }}
                  >
                    {tool.icon}
                  </span>
                ) : (
                  tool.icon
                )}
              </ToolIcon>
              <div
                style={{
                  marginTop: 18,
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#0f172a",
                  marginBottom: 8,
                  letterSpacing: "-0.2px",
                }}
              >
                {tool.name}
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: "#64748b",
                  lineHeight: 1.65,
                  fontWeight: 400,
                }}
              >
                {tool.desc}
              </div>
              <div
                style={{
                  marginTop: 18,
                  fontSize: 12,
                  fontWeight: 700,
                  color: hovered === tool.id ? "#3b82f6" : "#94a3b8",
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  transition: "color 0.15s",
                }}
              >
                Select tool →
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES STRIPE ────────────────────────────────────── */}
      <section
        style={{
          background: "#fff",
          borderTop: "1px solid #e2e8f0",
          borderBottom: "1px solid #e2e8f0",
          padding: "56px 28px",
        }}
      >
        <div style={{ maxWidth: 1280, margin: "0 auto" }}>
          <h2
            style={{
              textAlign: "center",
              fontSize: 28,
              fontWeight: 800,
              color: "#0f172a",
              marginBottom: 8,
              letterSpacing: "-0.5px",
            }}
          >
            Why choose FreePDFMaster.in?
          </h2>
          <p
            style={{
              textAlign: "center",
              color: "#64748b",
              marginBottom: 44,
              fontSize: 15,
            }}
          >
            Built for speed, privacy, and ease of use
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))",
              gap: 24,
            }}
          >
            {FEATURES.map((f) => (
              <div
                key={f.title}
                className="feature-card"
                style={{
                  background: "#f8fafc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 16,
                  padding: "28px 24px",
                  transition: "all 0.2s",
                }}
              >
                <div style={{ fontSize: 36, marginBottom: 14 }}>{f.icon}</div>
                <div
                  style={{
                    fontWeight: 700,
                    fontSize: 16,
                    color: "#0f172a",
                    marginBottom: 8,
                  }}
                >
                  {f.title}
                </div>
                <div
                  style={{ fontSize: 13, color: "#64748b", lineHeight: 1.65 }}
                >
                  {f.desc}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── UPGRADE BANNER (free users only) ───────────────────── */}
      {user?.plan === "free" && (
        <section
          style={{
            background: "linear-gradient(135deg,#1e1b4b,#312e81,#1e1b4b)",
            padding: "48px 28px",
            textAlign: "center",
          }}
        >
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <div style={{ fontSize: 13, color: "#a5b4fc", fontWeight: 700, marginBottom: 12, letterSpacing: 1, textTransform: "uppercase" }}>
              ✨ Upgrade to Pro
            </div>
            <h2 style={{ fontSize: 30, fontWeight: 800, color: "#fff", marginBottom: 16, letterSpacing: "-0.5px" }}>
              Remove limits and work without interruption
            </h2>
            <p style={{ color: "#c7d2fe", fontSize: 15, marginBottom: 32, lineHeight: 1.7 }}>
              Free plan includes 5 exports/month. Pro gives you unlimited exports, no ads, and up to 200 MB file size.
            </p>
            <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap", marginBottom: 28 }}>
              {["✅ Unlimited exports", "✅ No advertisements", "✅ 200 MB file size", "✅ Priority support"].map((f) => (
                <div key={f} style={{ fontSize: 14, color: "#a5b4fc", fontWeight: 600 }}>{f}</div>
              ))}
            </div>
            <a
              href="mailto:upgrade@freepdftmaster.in?subject=Pro Upgrade - FreePDFMaster.in"
              style={{
                display: "inline-block",
                padding: "15px 40px",
                background: "linear-gradient(135deg,#f59e0b,#d97706)",
                color: "#fff",
                borderRadius: 12,
                fontWeight: 800,
                textDecoration: "none",
                fontSize: 16,
                boxShadow: "0 6px 24px rgba(245,158,11,0.35)",
              }}
            >
              Upgrade to Pro — $9/month →
            </a>
          </div>
        </section>
      )}

      {/* ── FOOTER ─────────────────────────────────────────────── */}
      <footer
        style={{
          borderTop: "1px solid #e2e8f0",
          padding: "28px 28px",
          background: "#fff",
        }}
      >
        <div
          style={{
            maxWidth: 1280,
            margin: "0 auto",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 12,
          }}
        >
          <div style={{ fontSize: 13, color: "#94a3b8" }}>
            © {new Date().getFullYear()} FreePDFMaster.in · All PDF tools, free forever
          </div>
          <div style={{ display: "flex", gap: 20, fontSize: 13 }}>
            {["Privacy Policy", "Terms of Use", "Contact"].map((l) => (
              <span
                key={l}
                style={{ color: "#94a3b8", cursor: "pointer" }}
                onMouseEnter={(e) => (e.currentTarget.style.color = "#3b82f6")}
                onMouseLeave={(e) => (e.currentTarget.style.color = "#94a3b8")}
              >
                {l}
              </span>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}
